import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CLAiChatApi } from 'api/CL/CLAiChatApi';
import { CLAiChatHistoryItem, CLLegacyAiChatHistoryItem } from 'models/CL/CLApiTypes';

const getAxiosErrorMessage = (error: unknown) => {
    if (!axios.isAxiosError(error)) {
        return error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다';
    }

    const responseData = error.response?.data as { message?: string; error?: { message?: string } } | undefined;
    return [
        error.response?.status,
        responseData?.message || responseData?.error?.message || error.message,
    ].filter(Boolean).join(' ');
};

const toIsoDate = (timestamp?: number) => {
    if (!timestamp) return undefined;
    return new Date(timestamp).toISOString();
};

const normalizeLegacyHistory = (item: CLLegacyAiChatHistoryItem): CLAiChatHistoryItem[] => {
    const userContent: CLAiChatHistoryItem['content'] = [];
    const assistantContent: CLAiChatHistoryItem['content'] = [];

    if (item.reqMsg) {
        userContent.push({ type: 'text', text: item.reqMsg });
    }
    if (item.imageFileName || item.imagePath) {
        userContent.push({
            type: 'image',
            fileName: item.imageFileName || item.imagePath,
            url: item.imagePath,
        });
    }
    if (item.resMsg) {
        assistantContent.push({ type: 'text', text: item.resMsg });
    }
    if (item.subResult && item.subResult.length > 0) {
        assistantContent.push({
            type: 'tool_result',
            output: item.subResult,
        });
    }

    const history: Array<CLAiChatHistoryItem | null> = [
        userContent.length > 0 ? {
            _id: `${item._id || item.timestamp}-request`,
            userId: item.userId,
            hospitalId: item.hospitalId,
            role: 'user',
            content: userContent,
            createdAt: toIsoDate(item.reqTimestamp || item.timestamp),
            source: 'legacy',
        } : null,
        assistantContent.length > 0 ? {
            _id: `${item._id || item.timestamp}-response`,
            userId: item.userId,
            hospitalId: item.hospitalId,
            role: 'assistant',
            content: assistantContent,
            createdAt: toIsoDate(item.resTimestamp || item.timestamp),
            useTokens: item.useTokens,
            isReported: item.isReported,
            reportReason: item.reportReason,
            source: 'legacy',
        } : null,
    ];

    return history.filter((historyItem): historyItem is CLAiChatHistoryItem => !!historyItem);
};

const historyKey = (item: CLAiChatHistoryItem) => [
    item.source || 'v2',
    item._id || '',
    item.userId,
    item.role,
    item.createdAt || '',
].join(':');

export const useGetAiChatHistoryQueries = (userIds: string[], days: number) => {
    const clAdminToken = process.env.REACT_APP_CL_ADMIN_KEY;
    const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));

    return useQuery({
        queryKey: ['getCLAiChatHistoryQueries', uniqueUserIds, days],
        queryFn: async () => {
            try {
                const results = await Promise.all(uniqueUserIds.map(async (userId) => {
                    const [v2Response, legacyResponse] = await Promise.all([
                        CLAiChatApi.getAiChatHistory(userId, days, clAdminToken!),
                        CLAiChatApi.getLegacyAiChatHistory(userId, days, clAdminToken!),
                    ]);

                    if (v2Response.status !== 'ok') {
                        const responseMessage = (v2Response as unknown as { message?: string; error?: { message?: string } }).message
                            || (v2Response as unknown as { message?: string; error?: { message?: string } }).error?.message;
                        throw new Error(responseMessage || `AI 채팅 v2 응답 데이터가 없습니다. status=${v2Response.status || 'unknown'}`);
                    }
                    if (legacyResponse.status !== 'ok') {
                        const responseMessage = (legacyResponse as unknown as { message?: string; error?: { message?: string } }).message
                            || (legacyResponse as unknown as { message?: string; error?: { message?: string } }).error?.message;
                        throw new Error(responseMessage || `AI 채팅 기존 응답 데이터가 없습니다. status=${legacyResponse.status || 'unknown'}`);
                    }

                    const v2History = (v2Response.data || []).map((item) => ({ ...item, source: 'v2' as const }));
                    const legacyHistory = (legacyResponse.data || []).flatMap(normalizeLegacyHistory);
                    return [...v2History, ...legacyHistory];
                }));

                const historyMap = new Map<string, CLAiChatHistoryItem>();
                results.flat().forEach((item) => {
                    historyMap.set(historyKey(item), item);
                });

                return Array.from(historyMap.values());
            } catch (error) {
                throw new Error(getAxiosErrorMessage(error));
            }
        },
        enabled: uniqueUserIds.length > 0 && !!clAdminToken,
        retry: 1,
        refetchOnWindowFocus: false,
    });
};
